import { useEffect, useState } from 'react';

export type UseCountUpOptions = {
  /** Count up only the first time this key is seen in the browser session (all figures in a strip share one key). */
  sessionKey?: string;
  /** Start from this share of the value, so a live figure recounts rather than dropping to zero. */
  fromRatio?: number;
  /** Stagger between figures. */
  delayMs?: number;
};

const FALLBACK_MS = 600;

function countDuration(): number {
  if (typeof document === 'undefined') return FALLBACK_MS;
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--jc-duration-count').trim();
  const ms = raw.endsWith('ms') ? parseFloat(raw) : raw.endsWith('s') ? parseFloat(raw) * 1000 : NaN;
  return Number.isFinite(ms) && ms > 0 ? ms : FALLBACK_MS;
}

function firstTimeThisSession(key: string): boolean {
  try {
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * A number that counts up to `value` once (duration from --jc-duration-count). The server and the
 * first client render show the real value, so markup never disagrees; the recount starts after
 * mount, only once per session per key, and never under reduced motion.
 */
export function useCountUp(
  value: number,
  { sessionKey, fromRatio = 0.9, delayMs = 0 }: UseCountUpOptions = {},
): number {
  const [shown, setShown] = useState(value);
  useEffect(() => {
    setShown(value);
    if (typeof window === 'undefined') return undefined;
    const reduced =
      typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || !sessionKey || !firstTimeThisSession(`${sessionKey}:${delayMs}`)) return undefined;
    const from = value * fromRatio;
    const duration = countDuration();
    let frame = 0;
    let start = 0;
    setShown(from);
    const timer = setTimeout(() => {
      const step = (t: number) => {
        if (!start) start = t;
        const p = Math.min(1, (t - start) / duration);
        setShown(from + (value - from) * (1 - Math.pow(1 - p, 3)));
        if (p < 1) frame = requestAnimationFrame(step);
      };
      frame = requestAnimationFrame(step);
    }, delayMs);
    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
    };
  }, [value, sessionKey, fromRatio, delayMs]);
  return shown;
}
